<script>
import { planningEmits, planningProps, usePlanning } from "./mixin";
import Button from "@/components/atoms/button";
import Icon from "@/components/atoms/icon";
import Badge from "@/components/atoms/badge";
import Divider from "@/components/atoms/divider";
import DropDown from "@/components/molecules/drop-down";
import DropDownItem from "@/components/molecules/drop-down/drop-down-item";
import List from "@/components/molecules/list";
import ListItem from "@/components/molecules/list/list-item";
import PopinAnchor from "@/components/molecules/popin-anchor";
import GridRow from "@/components/molecules/grid/grid-row";
import GridCol from "@/components/molecules/grid/grid-col";
import Alert from "@/components/molecules/alert";
import FormCheckbox from "@/components/molecules/form-checkbox";
import LoadingSpinner from "@/components/molecules/loading-spinner";
import LazyImage from "@/components/atoms/lazy-image";
import Titre from "@/components/atoms/titre";

export default {
  name: "Planning",
  components: {
    Button,
    DropDown,
    DropDownItem,
    ListItem,
    List,
    Divider,
    GridCol,
    GridRow,
    Badge,
    PopinAnchor,
    Icon,
    Alert,
    FormCheckbox,
    LoadingSpinner,
    LazyImage,
    Titre
  },
  props: {
    ...planningProps()
  },
  emits: [...planningEmits()],
  setup(props, context) {
    const {
      hasParapharmacie,
      hasOnlyParapharmacie,
      selectedDate,
      selectedCreneau,
      momentJournee,
      cgu,
      pdp,
      displayMomentJournee,
      firstDates,
      dropDownDates,
      isDropDownSelected,
      planningByMoment,
      infosLegalesFormatted,
      slotButtonTag,
      hasCreneauDispo,
      dateSelected,
      creneauSelected,
      isDateSelected,
      isCreneauSelected,
      creneauValidated,
      getDatesIndispo,
      changeCreneau,
      deleteProduitsIndispos
    } = usePlanning(props, context);

    return {
      selectedDate,
      selectedCreneau,
      momentJournee,
      cgu,
      pdp,
      displayMomentJournee,
      firstDates,
      dropDownDates,
      isDropDownSelected,
      planningByMoment,
      infosLegalesFormatted,
      slotButtonTag,
      hasCreneauDispo,
      dateSelected,
      creneauSelected,
      isDateSelected,
      isCreneauSelected,
      creneauValidated,
      getDatesIndispo,
      changeCreneau,
      deleteProduitsIndispos,
      hasParapharmacie,
      hasOnlyParapharmacie
    };
  }
};
</script>

<template>
  <div class="c-planning u-ml-md u-mr-md">
    <div class="c-planning__date u-mb-md  u-ml-lg25 u-mr-lg25">
      <Button
        v-for="date in firstDates"
        :key="date.id"
        :selected="isDateSelected(date.id)"
        :aria-current="isDateSelected(date.id)"
        outline
        radius="full"
        size="md"
        class="u-flex-column u-font-500 u-mr-md"
        @click="dateSelected(date)"
      >
        {{ dayPlanning(date.attributes.date_du_jour) }}
        <br />
        <span class="u-font-14">
          {{ datePlanning(date.attributes.date_du_jour) }}
        </span>
      </Button>
      <DropDown
        v-if="dropDownDates.length > 0"
        :selected="isDropDownSelected"
        :aria-current="isDropDownSelected"
        radius="full"
        outline
        size="md"
        class="c-planning-date__drop-down"
        button-class="u-full-height"
        scrollable
      >
        <template #button-content>
          {{
            isDropDownSelected
              ? selectedDate.attributes.date_texte
              : "Autres dates"
          }}
        </template>
        <DropDownItem
          v-for="(date, index) in dropDownDates"
          :key="date.id"
        >
          <Button
            class="c-planning-date-dd__button u-font-14"
            @click="dateSelected(date)"
          >
            {{ date.attributes.date_texte }}
          </Button>
          <Divider
            v-if="index !== dropDownDates.length - 1"
            size="md"
            class="c-card__divider"
          />
        </DropDownItem>
      </DropDown>
    </div>

    <div
      v-if="selectedDate"
      class="c-planning-slots u-ml-lg25 u-mr-lg25"
      :class="creneauxFullWidth && 'c-planning-slots--large'"
    >
      <Divider dotted />
      <Alert
        v-if="produitsIndispos.length > 0 && hasCreneauDispo"
        class="u-radius-md u-pt-lg u-px-xlg u-pb-lg20 u-my-md u-pt-sm@md u-px-sm@md"
      >
        <div class="u-flex u-flex-column">
          <Titre
            tag="h3"
            size="xxs"
            variant="primary"
            class="u-mb-sm"
          >
            Ces produits ne sont pas disponibles pour le jour sélectionné
          </Titre>
          <p
            v-if="hasOnlyParapharmacie"
            class="u-font-16 u-mb-lg"
          >
            Votre Parapharmacie prend une petite pause le dimanche.<br>
            Les produits ci-dessous ne sont pas disponibles le jour sélectionné.
          </p>
          <p
            v-else-if="hasParapharmacie"
            class="u-font-16 u-mb-lg"
          >
            Le service parapharmacie et certains produits frais sont indisponibles le jour sélectionné.<br>
            Les produits ci-dessous ne sont donc pas disponibles.
          </p>
          <p
            v-else
            class="u-font-16 u-mb-lg"
          >
            Nos artisans s’assurent de la qualité de vos produits !<br>
            Pour garantir leur fraicheur, les produits ci-dessous ne sont pas disponibles le jour sélectionné.
          </p>

          <List class="u-font-14 u-flex u-flex-column u-gap-sm">
            <ListItem
              v-for="(produit, index) in produitsIndispos"
              :key="index"
              :produit="produit"
              class="u-flex u-flex-align-center u-gap-sm"
            >
              <LazyImage
                :src="produit.attributes.produit.data.attributes.visuel"
                width="64"
                height="64"
                alt=""
                class="u-flex-shrink-none"
              />
              <div>
                <p class="u-color-text-light u-mb-xxs">
                  {{ produit.attributes.designation }}
                </p>
                <p>
                  Indisponible le <span class="u-font-700">{{ getDatesIndispo(produit) }}</span>
                </p>
              </div>
            </ListItem>
          </List>
          <Divider
            dotted
            color="grey"
            class="u-mt-lg u-mb-md u-mx-none"
          />
          <p class="u-font-16">
            Vous pouvez changer de créneau pour conserver vos produits.<br>
            Ou bien supprimer vos produits pour conserver votre créneau.
          </p>
          <div class="u-flex u-flex-wrap@md u-flex-justify-center u-flex-self-center u-gap-lg20 u-gap-md@md u-mt-md">
            <Button
              size="md"
              radius="full"
              variant="primary"
              @click="changeCreneau"
            >
              Changer de créneau
            </Button>
            <Button
              v-if="!produitsIndisposOnly"
              size="md"
              radius="full"
              variant="primary"
              @click="deleteProduitsIndispos"
            >
              Supprimer mes produits
            </Button>
          </div>
        </div>
      </Alert>
      <GridRow v-else>
        <GridCol
          v-if="planningByMoment['matin'].length > 0"
          :class="creneauxFullWidth && 'u-ml-md u-mr-md'"
        >
          <p
            id="planningMatin"
            class="u-mb-md u-mt-md"
          >
            <span :class="[!displayMomentJournee && 'u-sr-only']">
              Matin
            </span>
          </p>
          <List
            :class="
              planningByMoment['matin'].length == 1
                ? 'c-planning-slots__list c-planning-slots__list--center'
                : 'c-planning-slots__list'
            "
            aria-labelledby="planningMatin"
          >
            <ListItem
              v-for="creneau in planningByMoment['matin']"
              :key="creneau.id"
              class="c-planning-slots__list-item"
            >
              <Button
                radius="md"
                size="xs"
                outline
                :disabled="!creneau.etat"
                :selected="isCreneauSelected(creneau)"
                :aria-current="isCreneauSelected(creneau)"
                :tag="slotButtonTag"
                class="c-planning-slots__button"
                :class="{
                  'c-planning-slots__button--affluence':
                    creneau.forte_affluence,
                }"
                @click="creneauSelected(creneau)"
              >
                <Icon
                  v-if="creneau.forte_affluence && creneau.etat"
                  name="clock"
                  class="c-planning-slots__button-icon"
                  :variant="isCreneauSelected(creneau) ? 'white' : 'orange'"
                  size="xs"
                />
                <span class="c-planning-slots__times">
                  <span class="c-planning-slots__time">
                    {{ creneau.heure_debut }}
                    <template v-if="creneauxFullWidth">
                      à {{ creneau.heure_fin }}
                    </template>
                  </span>
                  <span
                    v-if="!creneauxFullWidth"
                    class="c-planning-slots__time"
                  >
                    {{ creneau.heure_fin }}
                  </span>
                  <span
                    v-if="creneau.forte_affluence"
                    class="u-sr-only"
                  >
                    Forte affluence
                  </span>
                </span>
              </Button>
            </ListItem>
          </List>
        </GridCol>
        <GridCol
          v-if="planningByMoment['aprem'].length > 0"
          :class="creneauxFullWidth && 'u-ml-md u-mr-md'"
        >
          <p
            id="planningAprem"
            class="u-mb-md u-mt-md"
          >
            <span :class="[!displayMomentJournee && 'u-sr-only']">
              Après-midi
            </span>
          </p>
          <List
            :class="
              planningByMoment['aprem'].length == 1
                ? 'c-planning-slots__list c-planning-slots__list--center'
                : 'c-planning-slots__list'
            "
            aria-labelledby="planningAprem"
          >
            <ListItem
              v-for="creneau in planningByMoment['aprem']"
              :key="creneau.id"
              class="c-planning-slots__list-item"
            >
              <Button
                radius="md"
                size="sm"
                outline
                :disabled="!creneau.etat"
                :selected="isCreneauSelected(creneau)"
                :aria-current="isCreneauSelected(creneau)"
                :tag="slotButtonTag"
                class="c-planning-slots__button"
                :class="{
                  'c-planning-slots__button--affluence':
                    creneau.forte_affluence,
                }"
                @click="creneauSelected(creneau)"
              >
                <Icon
                  v-if="creneau.forte_affluence && creneau.etat"
                  name="clock"
                  class="c-planning-slots__button-icon"
                  :variant="isCreneauSelected(creneau) ? 'white' : 'orange'"
                  size="xs"
                />
                <span class="c-planning-slots__times">
                  <span class="c-planning-slots__time">
                    {{ creneau.heure_debut }}
                    <template v-if="creneauxFullWidth">
                      à {{ creneau.heure_fin }}
                    </template>
                  </span>
                  <span
                    v-if="!creneauxFullWidth"
                    class="c-planning-slots__time"
                  >
                    {{ creneau.heure_fin }}
                  </span>
                  <span
                    v-if="creneau.forte_affluence"
                    class="u-sr-only"
                  >
                    Forte affluence
                  </span>
                </span>
              </Button>
            </ListItem>
          </List>
        </GridCol>
        <GridCol
          v-if="planningByMoment['soir'].length > 0"
          :class="creneauxFullWidth && 'u-ml-md u-mr-md'"
        >
          <p
            id="planningSoir"
            class="u-mb-md u-mt-md"
          >
            <span :class="[!displayMomentJournee && 'u-sr-only']">
              Soir
            </span>
          </p>
          <List
            :class="
              planningByMoment['soir'].length == 1
                ? 'c-planning-slots__list c-planning-slots__list--center'
                : 'c-planning-slots__list'
            "
            aria-labelledby="planningSoir"
          >
            <ListItem
              v-for="creneau in planningByMoment['soir']"
              :key="creneau.id"
              class="c-planning-slots__list-item"
            >
              <Button
                radius="md"
                size="sm"
                outline
                :disabled="!creneau.etat"
                :selected="isCreneauSelected(creneau)"
                :aria-current="isCreneauSelected(creneau)"
                :tag="slotButtonTag"
                class="c-planning-slots__button"
                :class="{
                  'c-planning-slots__button--affluence':
                    creneau.forte_affluence,
                }"
                @click="creneauSelected(creneau)"
              >
                <Icon
                  v-if="creneau.forte_affluence && creneau.etat"
                  name="clock"
                  class="c-planning-slots__button-icon"
                  :variant="isCreneauSelected(creneau) ? 'white' : 'orange'"
                  size="xs"
                />
                <span class="c-planning-slots__times">
                  <span class="c-planning-slots__time">
                    {{ creneau.heure_debut }}
                    <template v-if="creneauxFullWidth">
                      à {{ creneau.heure_fin }}
                    </template>
                  </span>
                  <span
                    v-if="!creneauxFullWidth"
                    class="c-planning-slots__time"
                  >
                    {{ creneau.heure_fin }}
                  </span>
                  <span
                    v-if="creneau.forte_affluence"
                    class="u-sr-only"
                  >
                    Forte affluence
                  </span>
                </span>
              </Button>
            </ListItem>
          </List>
        </GridCol>
      </GridRow>
    </div>
    <template v-if="produitsIndispos.length === 0 || !hasCreneauDispo">
      <div
        v-if="selectedDate"
        class="c-planning__legend u-ml-lg25 u-mr-lg25"
      >
        <div class="c-planning__legend-label">
          Légende des créneaux
        </div>
        <Badge
          class="c-planning__legend-badge c-planning__legend-badge--unavailable"
          disabled
          radius="md"
        >
          Non disponible
        </Badge>
        <Badge
          class="c-planning__legend-badge"
          radius="md"
          outline
        >
          Disponible
        </Badge>
        <PopinAnchor
          popin-size="sm"
          position="bottom"
          open-on-hover
          :hover-delay="100"
          aria-label="Forte affluence"
        >
          <template #button-content>
            <Badge
              radius="md"
              outline
              class="c-planning__legend-badge c-planning__legend-affluence"
            >
              <Icon
                name="clock"
                class="c-planning__icon"
                variant="orange"
              />
              Forte affluence
            </Badge>
          </template>
          <p>
            Si vous choisissez un créneau à forte affluence, il se peut qu’il y
            ait un peu d’attente.
          </p>
        </PopinAnchor>
      </div>
      <template v-if="service && (service.cgu || service.pdp) && selectable">
        <Divider dotted />
        <FormCheckbox
          v-if="service.cgu"
          v-model="cgu"
          class="u-mt-md u-mb-sm u-text-left"
        >
          <p class="u-color-grey-1 u-font-14">
            J'accepte les
            <Button
              link
              :href="service.cgu"
              target="_blank"
            >
              conditions générales d'utilisation
            </Button>
            du service {{ service.label }}
          </p>
        </FormCheckbox>
        <FormCheckbox
          v-if="service.pdp"
          v-model="pdp"
          class="u-mb-md u-text-left"
        >
          <p class="u-color-grey-1 u-font-14">
            Je déclare avoir pris connaissance de la
            <Button
              link
              :href="service.pdp"
              target="_blank"
            >
              politique de gestion des données personnelles
            </Button>
            de {{ service.label }} et l’accepter
          </p>
        </FormCheckbox>
      </template>
      <slot />
      <div
        v-if="isLoading"
        class="u-mb-sm"
      >
        <LoadingSpinner size="xs" />
        <p class="u-mt-sm">
          Validation du créneau sélectionné, merci de patienter ...
        </p>
      </div>
      <template v-else>
        <template v-if="selectable">
          <p
            v-if="selectedCreneau === null"
            class="u-font-700 u-text-center u-color-alert u-mb-sm"
          >
            Veuillez choisir un créneau pour poursuivre votre commande
          </p>
          <p
            v-else-if="sacsDriveIsError"
            class="u-font-700 u-text-center u-color-alert u-mb-sm"
          >
            Merci de préciser si vous avez besoin de sacs ou si vous souhaitez amener vos propres sacs pour ranger vos courses.
          </p>
          <p
            v-else-if="
              service && ((service.pdp && !pdp) || (service.cgu && !cgu))
            "
            class="u-font-700 u-text-center u-color-alert u-mb-sm"
          >
            Veuillez accepter les mentions ci-dessus pour poursuivre votre
            commande
          </p>
        </template>
        <Button
          v-if="selectable"
          variant="secondary"
          radius="full"
          size="md"
          class="u-mb-lg"
          :disabled="
            selectedCreneau === null ||
              (service &&
                ((service.pdp != null && service.pdp != '' && !pdp) ||
                  (service.cgu != null && service.cgu != '' && !cgu)))
          "
          @click="creneauValidated()"
        >
          Je valide ce créneau
        </Button>
        <Alert
          v-else
          has-icon
          icon-name="info-circle"
          icon-color="primary-light"
          icon-size="sm"
          radius="md"
        >
          Vous pourrez sélectionner votre créneau horaire au moment de la
          finalisation de la commande
        </Alert>
        <p
          v-if="infosLegalesFormatted && selectedCreneau !== null"
          class="u-mb-lg u-text-xs u-color-grey-3 u-font-13 u-text-left"
          v-html="infosLegalesFormatted"
        />
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/planning.scss";
</style>
